import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import {
  CartesianGrid,
  ComposedChart,
  XAxis,
  YAxis,
  Line,
  Area,
  ReferenceLine,
} from 'recharts'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardFooter,
} from '@/components/ui/card'
import { ChartTooltip } from '@/components/ui/chart'
import { ScrollArea, ScrollBar } from '@/components/ui/scroll-area'
import { Button } from '@/components/ui/button'
import { format } from 'date-fns'
import { getWeek } from '@/lib/date-utils'
import { useRoasStore } from '@/stores/roas'
import { TargetDays } from './base-virtualized-table'
import ScrollableChart from '@/components/zthd/ScrollableChart'
import {
  buildNiceTicks,
  DEFAULT_CHART_MARGIN,
} from '@/components/zthd/chart-axis-utils'

const CHART_MARGIN = DEFAULT_CHART_MARGIN
const Y_TICK_COUNT = 8

// --- Constants ---
const DAY_COLORS = {
  0: 'hsl(220, 70%, 50%)', // Blue
  1: 'hsl(230, 70%, 50%)',
  3: 'hsl(239, 80.70%, 34.50%)', // Green
  7: 'hsl(142, 76%, 36%)', // Green
  14: 'hsl(38, 92%, 50%)', // Orange
  30: 'hsl(262, 83%, 58%)', // Purple
  60: 'hsl(346, 77%, 49%)', // Red
  90: 'hsl(173, 58%, 39%)', // Teal
  120: 'hsl(43, 74%, 49%)', // Yellow
  150: 'hsl(280, 65%, 60%)', // Magenta
  180: 'hsl(17, 88%, 40%)', // Brown
}

// --- Helpers ---
const getDayColor = (day) => {
  return DAY_COLORS[day] || `hsl(${(day * 137.5) % 360}, 70%, 50%)`
}

const getCompareColor = (day, index = 0) => {
  const mainColor = getDayColor(day)
  // 解析 HSL 值（格式为 'hsl(h, s%, l%)'）
  const hslMatch = mainColor.match(
    /hsl\((\d+),\s*(\d+(?:\.\d+)?)%,\s*(\d+(?:\.\d+)?)%\)/,
  )
  if (!hslMatch) {
    // 如果解析失败，返回基于索引旋转色相的对比颜色
    return `hsl(${((day * 137.5) + (index * 30)) % 360}, 50%, 60%)`
  }
  const h = parseInt(hslMatch[1], 10)
  const s = parseFloat(hslMatch[2])
  const l = parseFloat(hslMatch[3])
  
  // 基于索引调整色相，使不同 campaign 颜色不同
  const newH = (h + index * 40) % 360
  // 降低饱和度，提高亮度，使其与主曲线区分
  const newS = Math.max(30, s - 20)
  const newL = Math.min(85, l + 10)
  return `hsl(${newH}, ${newS}%, ${newL}%)`
}

// --- Hooks ---
const useChartData = ({ roas, compareRoas, selectedDays, displayAdMode }) => {
  const showImpressions = displayAdMode === 'impressions'

  const { chartData, values } = useMemo(() => {
    if (!roas?.dailyData) return { chartData: [], values: [] }

    const collectedValues = []
    const data = roas.dailyData
      .map((row) => {
        const installDate = new Date(row.install_date)
        const today = new Date()
        const daysDiff = Math.floor(
          (today - installDate) / (1000 * 60 * 60 * 24),
        )

        // Format date with weekday
        const dateStr = row.install_date.replace(/-/g, '')
        const [, weekDay] = getWeek(dateStr)
        const formattedDate = format(new Date(row.install_date), 'MM-dd')

        const dataPoint = {
          date: `${formattedDate} 周${weekDay}`,
          install_date: new Date(row.install_date),
        }

        selectedDays.forEach((selectedDay) => {
          const days = row.days || {}
          const impressionDays = row.impressionDays || {}

          const impressions = Number(impressionDays[`day${selectedDay}`])
          const revenue = Number(days[`day${selectedDay}`])

          let hasData = false
          let value = 0

          if (showImpressions) {
            hasData =
              impressionDays[`day${selectedDay}`] !== undefined &&
              !isNaN(impressions)
            value = hasData ? impressions : 0
          } else {
            // ECPM mode
            hasData =
              impressionDays[`day${selectedDay}`] !== undefined &&
              days[`day${selectedDay}`] !== undefined &&
              impressions > 0
            value = hasData ? (revenue / impressions) * 1000 : 0
          }

          if (daysDiff < selectedDay || !hasData) {
            dataPoint[`day${selectedDay}`] = null
            dataPoint[`day${selectedDay}_hasData`] = false
            dataPoint[`day${selectedDay}_displayValue`] = '-'
          } else {
            dataPoint[`day${selectedDay}`] = value
            dataPoint[`day${selectedDay}_hasData`] = true
            dataPoint[`day${selectedDay}_displayValue`] = showImpressions
              ? value.toLocaleString()
              : new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4,
                }).format(value)

            collectedValues.push(value)
          }
        })

        return dataPoint
      })
      .sort((a, b) => a.install_date - b.install_date)

    // Merge compare data if exists
    if (compareRoas && (Array.isArray(compareRoas) ? compareRoas.length > 0 : compareRoas.dailyData)) {
      const compareCampaigns = Array.isArray(compareRoas) ? compareRoas : [compareRoas]
      const mergedData = [...data]
      const buildAdComparePoint = (row, idx) => {
        const installDate = new Date(row.install_date)
        const today = new Date()
        const daysDiff = Math.floor((today - installDate) / (1000 * 60 * 60 * 24))
        const compareDateStrForWeek = row.install_date.replace(/-/g, '')
        const [, compareWeekDay] = getWeek(compareDateStrForWeek)
        const compareFormattedDate = format(new Date(row.install_date), 'MM-dd')
        const compareDataPoint = {
          date: `${compareFormattedDate} 周${compareWeekDay}`,
          install_date: new Date(row.install_date),
        }
        selectedDays.forEach((selectedDay) => {
          const days = row.days || {}
          const impressionDays = row.impressionDays || {}
          const impressions = Number(impressionDays[`day${selectedDay}`])
          const revenue = Number(days[`day${selectedDay}`])
          let hasData = false
          let value = 0
          if (showImpressions) {
            hasData = impressionDays[`day${selectedDay}`] !== undefined && !isNaN(impressions)
            value = hasData ? impressions : 0
          } else {
            hasData = impressionDays[`day${selectedDay}`] !== undefined && days[`day${selectedDay}`] !== undefined && impressions > 0
            value = hasData ? (revenue / impressions) * 1000 : 0
          }
          if (daysDiff < selectedDay || !hasData) {
            compareDataPoint[`day${selectedDay}_compare_${idx}`] = null
            compareDataPoint[`day${selectedDay}_compare_${idx}_hasData`] = false
            compareDataPoint[`day${selectedDay}_compare_${idx}_displayValue`] = '-'
          } else {
            compareDataPoint[`day${selectedDay}_compare_${idx}`] = value
            compareDataPoint[`day${selectedDay}_compare_${idx}_hasData`] = true
            compareDataPoint[`day${selectedDay}_compare_${idx}_displayValue`] = showImpressions
              ? value.toLocaleString()
              : new Intl.NumberFormat('en-US', {
                  style: 'currency',
                  currency: 'USD',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 4,
                }).format(value)
            collectedValues.push(value)
          }
        })
        return compareDataPoint
      }
      const compareCampaignDataMaps = compareCampaigns.map((c, idx) => {
        if (c.isDateCompare && c.dailyData?.length) {
          const sorted = [...c.dailyData].sort(
            (a, b) => new Date(a.install_date) - new Date(b.install_date),
          )
          return sorted.map((row) => buildAdComparePoint(row, idx))
        }
        const map = new Map()
        c?.dailyData?.forEach((row) => {
          map.set(row.install_date, buildAdComparePoint(row, idx))
        })
        return map
      })

      mergedData.forEach((mainPoint, mainIndex) => {
        const mainDateStr = format(mainPoint.install_date, 'yyyy-MM-dd')
        compareCampaignDataMaps.forEach((mapOrArray, idx) => {
          const comparePoint = compareCampaigns[idx].isDateCompare
            ? (Array.isArray(mapOrArray) ? mapOrArray[mainIndex] ?? null : null)
            : mapOrArray.get(mainDateStr)
          selectedDays.forEach((selectedDay) => {
            if (comparePoint) {
              mainPoint[`day${selectedDay}_compare_${idx}`] = comparePoint[`day${selectedDay}_compare_${idx}`]
              mainPoint[`day${selectedDay}_compare_${idx}_hasData`] = comparePoint[`day${selectedDay}_compare_${idx}_hasData`]
              mainPoint[`day${selectedDay}_compare_${idx}_displayValue`] = comparePoint[`day${selectedDay}_compare_${idx}_displayValue`]
            } else {
              mainPoint[`day${selectedDay}_compare_${idx}`] = null
              mainPoint[`day${selectedDay}_compare_${idx}_hasData`] = false
              mainPoint[`day${selectedDay}_compare_${idx}_displayValue`] = '-'
            }
          })
          if (comparePoint && !compareCampaigns[idx].isDateCompare) {
            mapOrArray.delete(mainDateStr)
          }
        })
      })

      compareCampaignDataMaps.forEach((mapOrArray, idx) => {
        if (compareCampaigns[idx].isDateCompare || !(mapOrArray instanceof Map)) return
        mapOrArray.forEach((comparePoint, dateStr) => {
          let existingPoint = mergedData.find(p => format(p.install_date, 'yyyy-MM-dd') === dateStr)
          if (!existingPoint) {
            existingPoint = {
              date: comparePoint.date,
              install_date: comparePoint.install_date,
            }
            selectedDays.forEach((selectedDay) => {
              existingPoint[`day${selectedDay}`] = null
              existingPoint[`day${selectedDay}_hasData`] = false
              existingPoint[`day${selectedDay}_displayValue`] = '-'
              compareCampaigns.forEach((_, cIdx) => {
                existingPoint[`day${selectedDay}_compare_${cIdx}`] = null
                existingPoint[`day${selectedDay}_compare_${cIdx}_hasData`] = false
                existingPoint[`day${selectedDay}_compare_${cIdx}_displayValue`] = '-'
              })
            })
            mergedData.push(existingPoint)
          }
          selectedDays.forEach((selectedDay) => {
            existingPoint[`day${selectedDay}_compare_${idx}`] = comparePoint[`day${selectedDay}_compare_${idx}`]
            existingPoint[`day${selectedDay}_compare_${idx}_hasData`] = comparePoint[`day${selectedDay}_compare_${idx}_hasData`]
            existingPoint[`day${selectedDay}_compare_${idx}_displayValue`] = comparePoint[`day${selectedDay}_compare_${idx}_displayValue`]
          })
        })
      })

      return {
        chartData: mergedData.sort((a, b) => a.install_date - b.install_date),
        values: collectedValues,
      }
    }

    return { chartData: data, values: collectedValues }
  }, [
    roas?.dailyData,
    compareRoas,
    selectedDays,
    displayAdMode,
    showImpressions,
  ])

  // Y-Axis Calculations
  const { yMin, yMax } = useMemo(() => {
    const minValue = values.length > 0 ? Math.min(0, ...values) : 0
    const maxValue = values.length > 0 ? Math.max(0, ...values) : 100
    const valueRange = maxValue - minValue || 100
    const padding = valueRange * 0.2
    const yMaxRaw = maxValue + padding
    const yMinRaw = minValue - padding

    // For impressions (integers), round to nearest 10/100/etc
    // For ECPM (currency), round to nearest dollar/cent
    if (showImpressions) {
      const magnitude = Math.pow(10, Math.floor(Math.log10(valueRange || 1)))
      return {
        yMax: Math.ceil(yMaxRaw / magnitude) * magnitude,
        yMin: Math.max(0, Math.floor(yMinRaw / magnitude) * magnitude),
      }
    } else {
      return {
        yMax: Math.ceil(yMaxRaw),
        yMin: Math.max(0, Math.floor(yMinRaw)),
      }
    }
  }, [values, showImpressions])

  return { chartData, yMin, yMax }
}

// --- Sub-Components ---
const CustomTooltip = ({ active, payload, selectedDays, compareRoas }) => {
  if (!active || !payload?.length) return null
  const data = payload[0].payload
  const compareCampaigns = Array.isArray(compareRoas) ? compareRoas : (compareRoas ? [compareRoas] : [])

  return (
    <div className="rounded-lg border bg-background p-2 shadow-sm">
      <div className="flex flex-col gap-1">
        <span className="text-[0.70rem] uppercase text-muted-foreground">
          {data.date}
        </span>
        {selectedDays.map((day) => {
          const value = data[`day${day}_displayValue`]
          return (
            <React.Fragment key={day}>
              {value && value !== '-' && (
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: getDayColor(day) }}
                  />
                  <span className="text-xs">Day-{day}:</span>
                  <span className="font-bold text-xs">{value}</span>
                </div>
              )}
              {/* Compare data */}
              {compareCampaigns.map((c, idx) => {
                const compareValue = data[`day${day}_compare_${idx}_displayValue`]
                if (!compareValue || compareValue === '-') return null
                return (
                  <div key={`${day}_compare_${idx}`} className="flex items-center gap-2">
                    <div
                      className="w-2 h-2 rounded-full border-2 border-dashed"
                      style={{
                        backgroundColor: 'transparent',
                        borderColor: getCompareColor(day, idx),
                      }}
                    />
                    <span className="text-xs">Day-{day} ({c?.campaignName || 'Compare'}):</span>
                    <span className="font-bold text-xs">{compareValue}</span>
                  </div>
                )
              })}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}

// --- Main Component ---
const AdChart = ({ roas, compareRoas, dayIndex = 0 }) => {
  const { displayAdMode } = useRoasStore()
  const [selectedDays, setSelectedDays] = useState([0])
  const scrollAreaRef = useRef(null)

  const maxDay = useMemo(() => {
    const mainMaxDay = roas?.dailyData ? roas.dailyData.length - 1 : 0
    const compareMaxDay = compareRoas?.dailyData
      ? compareRoas.dailyData.length - 1
      : 0
    return Math.max(mainMaxDay, compareMaxDay)
  }, [roas?.dailyData, compareRoas?.dailyData])

  const scrollToDay = useCallback((day) => {
    if (scrollAreaRef.current) {
      const scrollArea = scrollAreaRef.current
      let viewport =
        scrollArea.querySelector('[data-radix-scroll-area-viewport]') ||
        scrollArea.querySelector('div[style*="overflow"]') ||
        scrollArea.firstElementChild

      if (viewport) {
        let targetTab = viewport.querySelector(`button[value="${day}"]`)

        if (!targetTab) {
          const allTabs = viewport.querySelectorAll('button')
          for (const tab of allTabs) {
            if (tab.textContent?.includes(`Day${day}`)) {
              targetTab = tab
              break
            }
          }
        }

        if (targetTab) {
          const tabOffsetLeft = targetTab.offsetLeft
          const tabWidth = targetTab.offsetWidth
          const viewportWidth = viewport.offsetWidth || viewport.clientWidth
          const scrollLeft = tabOffsetLeft - viewportWidth / 2 + tabWidth / 2

          if (viewport.scrollTo) {
            viewport.scrollTo({
              left: Math.max(0, scrollLeft),
              behavior: 'smooth',
            })
          } else {
            viewport.scrollLeft = Math.max(0, scrollLeft)
          }
        }
      }
    }
  }, [])

  const handleDaySelect = (day) => {
    setSelectedDays((prev) => {
      if (prev.includes(day)) {
        return prev.length > 1 ? prev.filter((d) => d !== day) : prev
      } else {
        return [...prev, day].sort((a, b) => a - b)
      }
    })
    setTimeout(() => scrollToDay(day), 100)
  }

  const handleToggleAllDays = () => {
    const availableDays = TargetDays.filter((day) => day <= maxDay)
    if (selectedDays.length === availableDays.length) {
      setSelectedDays(availableDays.length > 0 ? [availableDays[0]] : [])
    } else {
      setSelectedDays([...availableDays])
    }
  }

  useEffect(() => {
    if (dayIndex !== undefined && !selectedDays.includes(dayIndex)) {
      setSelectedDays([dayIndex])
      setTimeout(() => scrollToDay(dayIndex), 200)
    }
  }, [dayIndex])

  const { chartData, yMin, yMax } = useChartData({
    roas,
    compareRoas,
    selectedDays,
    displayAdMode,
  })

  const yTicks = useMemo(
    () => buildNiceTicks(yMin, yMax, Y_TICK_COUNT, { mode: 'auto' }),
    [yMin, yMax],
  )

  const chartConfig = useMemo(() => {
    const config = {}
    selectedDays.forEach((day) => {
      config[`day${day}`] = {
        label: `Day ${day}`,
        color: getDayColor(day),
      }
      // Add compare data config
      if (compareRoas) {
        const compareCampaigns = Array.isArray(compareRoas) ? compareRoas : [compareRoas]
        compareCampaigns.forEach((c, idx) => {
          config[`day${day}_compare_${idx}`] = {
            label: `Day ${day} (${c?.campaignName || 'Compare'})`,
            color: getCompareColor(day, idx),
          }
        })
      }
    })
    return config
  }, [selectedDays, compareRoas])

  if (!roas?.dailyData) return null

  const showImpressions = displayAdMode === 'impressions'
  const chartWidth = Math.max(
    chartData.length * 12,
    window.innerWidth * 0.5,
    600,
  )
  const availableDays = TargetDays.filter((day) => day <= maxDay)

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle>
            Day-{selectedDays.join(', ')}{' '}
            {showImpressions ? 'Impressions' : 'ECPM'}
            {compareRoas && (Array.isArray(compareRoas) ? compareRoas.length > 0 : true) &&
              ` (Compare: ${(Array.isArray(compareRoas) ? compareRoas : [compareRoas]).map(c => c.campaignName || 'Compare').join(', ')})`}
          </CardTitle>
        </div>
        <div className="flex flex-wrap items-center gap-2 mt-4">
          <span className="text-sm text-muted-foreground self-center">
            Days:
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={handleToggleAllDays}
            className="h-7 px-2 text-xs"
          >
            {selectedDays.length === availableDays.length
              ? 'Deselect All'
              : 'Select All'}
          </Button>
          {TargetDays.map((day) => (
            <Button
              key={day}
              variant={selectedDays.includes(day) ? 'default' : 'outline'}
              size="sm"
              onClick={() => handleDaySelect(day)}
              disabled={day > maxDay}
              className="h-7 px-2 text-xs"
              style={
                selectedDays.includes(day)
                  ? {
                      backgroundColor: getDayColor(day),
                      borderColor: getDayColor(day),
                      color: 'white',
                    }
                  : {}
              }
            >
              Day{day}
            </Button>
          ))}
        </div>
      </CardHeader>
      <CardContent>
        <ScrollableChart
          config={chartConfig}
          width={chartWidth}
          height={600}
          chartMargin={CHART_MARGIN}
          yAxis={{
            min: yMin,
            max: yMax,
            tickCount: Y_TICK_COUNT,
            ticks: yTicks,
            formatter: (value) => {
              if (showImpressions) return value.toLocaleString()
              return new Intl.NumberFormat('en-US', {
                style: 'currency',
                currency: 'USD',
                notation: 'compact',
                maximumFractionDigits: 1,
              }).format(value)
            },
            showPercentage: false,
          }}
        >
          <ComposedChart
            data={chartData}
            margin={CHART_MARGIN}
          >
            <CartesianGrid vertical={false} className="stroke-muted" />
            <XAxis
              dataKey="date"
              tickLine={false}
              axisLine={false}
              tickMargin={8}
              className="fill-muted-foreground text-sm"
              tickFormatter={(value) => value.split(' 周')[0]}
            />
            <YAxis
              type="number"
              tickLine={false}
              axisLine={false}
              domain={[yMin, yMax]}
              ticks={yTicks}
              allowDataOverflow
              width={0}
            />
            <ChartTooltip
              content={
                <CustomTooltip
                  selectedDays={selectedDays}
                  compareRoas={compareRoas}
                />
              }
            />

            <defs>
              {selectedDays.map((day) => (
                <linearGradient
                  key={day}
                  id={`colorDay${day}`}
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor={getDayColor(day)}
                    stopOpacity={0.5}
                  />
                  <stop
                    offset="95%"
                    stopColor={getDayColor(day)}
                    stopOpacity={0.1}
                  />
                </linearGradient>
              ))}
              {/* Compare data gradients */}
              {compareRoas &&
                selectedDays.map((day) => {
                  const compareCampaigns = Array.isArray(compareRoas) ? compareRoas : [compareRoas]
                  return compareCampaigns.map((_, idx) => (
                    <linearGradient
                      key={`compareDay${day}_${idx}`}
                      id={`colorDay${day}_compare_${idx}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={getCompareColor(day, idx)}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={getCompareColor(day, idx)}
                        stopOpacity={0.1}
                      />
                    </linearGradient>
                  ))
                })}
            </defs>

            {selectedDays.map((day) => (
              <React.Fragment key={day}>
                <Area
                  type="monotone"
                  dataKey={`day${day}`}
                  stroke={getDayColor(day)}
                  fill={`url(#colorDay${day})`}
                  strokeWidth={2}
                  connectNulls={false}
                  dot={{ fill: getDayColor(day), r: 3 }}
                  activeDot={{ r: 5, fill: getDayColor(day) }}
                />
                {/* Compare data area */}
                {compareRoas && (
                  <React.Fragment>
                    {(Array.isArray(compareRoas) ? compareRoas : [compareRoas]).map((_, idx) => (
                      <Area
                        key={`day${day}_compare_${idx}`}
                        type="monotone"
                        dataKey={`day${day}_compare_${idx}`}
                        stroke={getCompareColor(day, idx)}
                        fill={`url(#colorDay${day}_compare_${idx})`}
                        strokeWidth={2}
                        strokeDasharray="5 5"
                        connectNulls={false}
                        dot={{ fill: getCompareColor(day, idx), r: 3 }}
                        activeDot={{ r: 5, fill: getCompareColor(day, idx) }}
                        fillOpacity={0.2}
                      />
                    ))}
                  </React.Fragment>
                )}
              </React.Fragment>
            ))}
          </ComposedChart>
        </ScrollableChart>
      </CardContent>
      <CardFooter className="flex flex-col gap-3">
        <ScrollArea ref={scrollAreaRef} className="w-full h-12">
          <div className="w-max">
            <div className="inline-flex h-9 items-center justify-start rounded-lg bg-muted p-1 text-muted-foreground gap-1">
              {Array.from({ length: maxDay + 1 }, (_, i) => (
                <Button
                  key={i}
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDaySelect(i)}
                  className="h-7 px-2 text-xs whitespace-nowrap flex-shrink-0 data-[selected=true]:bg-primary data-[selected=true]:text-primary-foreground"
                  data-selected={selectedDays.includes(i)}
                  style={
                    selectedDays.includes(i)
                      ? {
                          backgroundColor: getDayColor(i),
                          color: 'white',
                        }
                      : {}
                  }
                >
                  Day{i}
                </Button>
              ))}
            </div>
          </div>
          <ScrollBar orientation="horizontal" />
        </ScrollArea>
      </CardFooter>
    </Card>
  )
}

export default AdChart
